import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import IngresoCobroPanel from '../components/IngresoCobroPanel';
import IngresoDetalleDrawer from '../components/IngresoDetalleDrawer';

function clp(n) {
  return `$${Number(n || 0).toLocaleString('es-CL')}`;
}

function CeldaMes({ celda }) {
  if (!celda) return '—';
  if (celda.sinPeriodo || celda.sinCargo) {
    return <span className="celda-na" title="Sin cargo publicado">—</span>;
  }
  if (celda.sinCargoPublicado || celda.borrador) {
    return <span className="celda-borrador" title="Cargo en borrador">…</span>;
  }
  if (celda.pagado || celda.pendiente === 0) {
    return <span className="celda-pagada">0</span>;
  }
  return <span className="celda-pendiente">{clp(celda.pendiente)}</span>;
}

export default function CobranzaMesPage() {
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [detalleRow, setDetalleRow] = useState(null);
  const [pagoRow, setPagoRow] = useState(null);

  const load = useCallback(() => {
    if (!cid) return Promise.resolve();
    setError('');
    return api
      .get(`/gastos-comunes/condominios/${cid}/cobranza-mes`)
      .then((r) => {
        setData(r.data);
        setDetalleRow((prev) => {
          if (!prev) return null;
          return r.data.filas?.find((f) => f.id === prev.id) || null;
        });
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, [cid]);

  useEffect(() => {
    localStorage.setItem('gc_condominio_id', cid);
    load();
  }, [cid, load]);

  const abrirMesCobro = async () => {
    setError('');
    try {
      const r = await api.post(`/gastos-comunes/condominios/${cid}/mes/abrir`, {});
      setMsg(r.data?.mensaje || 'Mes de cobro abierto con cargos desde cuotas.');
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const poblarDemo = async () => {
    if (!window.confirm('¿Cargar datos demo?')) return;
    setError('');
    try {
      await api.post(`/gastos-comunes/condominios/${cid}/demo/poblar`);
      setMsg('Demo cargado.');
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const restaurarCargos = async () => {
    setError('');
    try {
      const r = await api.post(`/gastos-comunes/condominios/${cid}/cobranza/restaurar-cargos`, {});
      setMsg(r.data?.mensaje || 'Cargos restaurados.');
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const col1 = data?.columnas?.[0];
  const col2 = data?.columnas?.[1];
  const colAnteriores = data?.columnas?.[2];
  const pasos = data?.pasos;

  const abrirPago = (row, e) => {
    e?.stopPropagation();
    setDetalleRow(null);
    setPagoRow(row);
  };

  const abrirDetalle = (row) => {
    setPagoRow(null);
    setDetalleRow(row);
  };

  return (
    <div className="page-ingresos page-ingresos-fullbleed">
      <header className="ingresos-head">
        <div>
          <h1 className="page-title">Cobranza</h1>
          <p className="page-lead page-lead-compact">
            Mes en curso — cobramos <strong>{data?.periodoServicio?.label || '…'}</strong>
            {data?.mesCalendario?.label ? (
              <>
                {' '}
                (calendario <strong>{data.mesCalendario.label}</strong>)
              </>
            ) : null}
            . Columnas: <strong>{col1?.label}</strong> · <strong>{col2?.label}</strong> ·{' '}
            <strong>{colAnteriores?.label || 'Meses anteriores'}</strong>.{' '}
            <Link to={`/c/${cid}/cobranza/historial`}>Historial (resumen por mes) →</Link>
          </p>
        </div>
        <div className="ingresos-head-actions">
          {!pasos?.listoCobrar && (
            <button type="button" className="btn" onClick={abrirMesCobro}>
              Abrir mes de cobro
            </button>
          )}
          <button type="button" className="btn secondary" onClick={restaurarCargos}>
            Sincronizar cargos
          </button>
          <button type="button" className="btn-ghost demo-btn" onClick={poblarDemo}>
            Demo
          </button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {msg && <div className="alert alert-success">{msg}</div>}
      {data?.diagnostico?.sugerirRevisarDuplicados && (
        <div className="alert alert-warn">
          Hay cargos duplicados del mismo mes. En detalle (≡): anule la línea <strong>dup.</strong> con botón Anular
          activo; si está gris, <strong>Revertir</strong> el abono primero. No anule el cargo que ya tiene pagos sin
          revertir.
        </div>
      )}

      <div className="ingresos-table-shell">
        <table className="ingresos-table">
          <colgroup>
            <col className="col-w-nombre" />
            <col className="col-w-mes" />
            <col className="col-w-mes" />
            <col className="col-w-monto" />
            <col className="col-w-monto" />
            <col className="col-w-monto" />
            <col className="col-w-acc" />
          </colgroup>
          <thead>
            <tr>
              <th>Propietario</th>
              <th title={col1?.sub}>{col1?.label || 'En cobro'}</th>
              <th title={col2?.sub}>{col2?.label || 'Anterior'}</th>
              <th title={colAnteriores?.sub}>{colAnteriores?.label || 'Meses anteriores'}</th>
              <th>Deuda vencida</th>
              <th>Total</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {(data?.filas || []).map((row) => (
              <tr
                key={row.id}
                className={`ingresos-row ${detalleRow?.id === row.id ? 'ingresos-row-active' : ''}`}
                onClick={() => abrirDetalle(row)}
              >
                <td className="col-nombre">{row.titularNombre || '—'}</td>
                <td>
                  <CeldaMes celda={row.mesAnterior1} />
                </td>
                <td>
                  <CeldaMes celda={row.mesAnterior2} />
                </td>
                <td className={row.mesesAnterioresAcum > 0 ? 'celda-pendiente' : 'celda-pagada'}>
                  {row.mesesAnterioresAcum > 0 ? clp(row.mesesAnterioresAcum) : '0'}
                </td>
                <td className={row.deudaVencida > 0 ? 'celda-deuda-vencida' : ''}>{clp(row.deudaVencida)}</td>
                <td className="col-total">{clp(row.totalPagar)}</td>
                <td className="col-acciones-icons">
                  <div className="ingresos-action-group">
                    <button
                      type="button"
                      className="btn-icon"
                      title="Detalle"
                      aria-label="Detalle"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirDetalle(row);
                      }}
                    >
                      ≡
                    </button>
                    <button
                      type="button"
                      className="btn-icon btn-icon-pay"
                      title="Pago"
                      aria-label="Pago"
                      disabled={!row.totalPagar}
                      onClick={(e) => abrirPago(row, e)}
                    >
                      $
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data?.filas?.length && <p className="empty-hint">Sin propiedades. Use Demo o abra el mes de cobro.</p>}
      </div>

      {detalleRow && (
        <IngresoDetalleDrawer
          cid={cid}
          row={detalleRow}
          periodoServicioLabel={data?.periodoServicio?.label}
          onClose={() => setDetalleRow(null)}
          onRefresh={load}
          onRegistrarPago={(row) => {
            setDetalleRow(null);
            setPagoRow(row);
          }}
        />
      )}

      {pagoRow && (
        <IngresoCobroPanel
          cid={cid}
          row={pagoRow}
          periodoServicioLabel={data?.periodoServicio?.label}
          onClose={() => setPagoRow(null)}
          onPaid={() => {
            setPagoRow(null);
            setMsg('Pago registrado.');
            load();
          }}
        />
      )}
    </div>
  );
}
